import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, OnInit, HostListener } from '@angular/core';
import { UserDashboardService, Dashboard, ChartData, extractChartAuditFields, formatDashboardUserEmail } from '../../services/user-dashboard.service';
import { chartQueryFieldsFromApi } from '../../shared/chart-insights-drilldown.util';
import { normalizeHighchartsApiConfig } from '../../shared/chart-label.util';
import { Router } from '@angular/router';
import { ChartOverviewComponent } from './chart-overview/chart-overview.component';
import { NotificationService } from '../../services/notification.service';
import { ConfirmPopupService } from '../../services/confirm-popup.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';
import { formatDateTimeInTimezone } from '../../shared/chart-timezone.util';

@Component({
  selector: 'user-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, ChartOverviewComponent, MatTooltipModule, MatDialogModule],
  templateUrl: './user-dashboard.component.html',
  styleUrl: './user-dashboard.component.scss',
})
export class UserDashboardComponent implements OnInit {
  userDashboards: Dashboard[] = [];
  filteredDashboards: Dashboard[] = [];
  searchQuery: string = '';
  isLoading = false;
  selectedDashboard: Dashboard | null = null;
  isLoadingDashboard = false;
  showCardMenu = false;
  selectedMenuDashboard: Dashboard | null = null;

  constructor(
    private userDashboardService: UserDashboardService,
    private router: Router,
    private notificationService: NotificationService,
    private confirmPopupService: ConfirmPopupService
  ) {}

  ngOnInit() {
    this.filteredDashboards = [];
    this.loadDashboards();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.card-actions-menu')) {
      this.showCardMenu = false;
      this.selectedMenuDashboard = null;
    }
  }

  loadDashboards() {
    this.isLoading = true;
    this.userDashboardService.getAllDashboards().subscribe({
      next: (response: any) => {
        // Handle different response formats
        let dashboardsArray: Dashboard[] = [];

        if (Array.isArray(response)) {
          dashboardsArray = response;
        } else if (response && Array.isArray(response.data)) {
          dashboardsArray = response.data;
        } else if (response && response.dashboards && Array.isArray(response.dashboards)) {
          dashboardsArray = response.dashboards;
        } else if (response && response.data && Array.isArray(response.data.dashboards)) {
          dashboardsArray = response.data.dashboards;
        }

        // Map API response to Dashboard interface (API uses PascalCase)
        this.userDashboards = dashboardsArray.map((dashboard: any) => ({
          id: dashboard.DashboardId || dashboard.dashboard_id || dashboard.id,
          name: dashboard.DashboardName || dashboard.dashboard_name || dashboard.name,
          charts: dashboard.charts || [],
          layout: dashboard.Layout || dashboard.layout || '2-column',
          is_shared_with_all: dashboard.IsSharedWithAll || dashboard.is_shared_with_all || dashboard.is_shared || false,
          is_shared: dashboard.is_shared || false,
          owner_id: dashboard.owner_id || null,
          generated_by: dashboard.generated_by || null,
          generated_by_email: dashboard.UserEmail || dashboard.generated_by_email || null,
          updated_by: dashboard.updated_by || null,
          updated_by_email: dashboard.UpdatedByEmail || dashboard.updated_by_email || null,
          user_email: dashboard.UserEmail || null, // Created by email
          created_at: dashboard.CreatedAt || dashboard.created_at,
          updated_at: dashboard.UpdatedAt || dashboard.updated_at
        }));

        this.applySearchFilter();
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading dashboards:', error);
        // On error, show empty state
        this.userDashboards = [];
        this.isLoading = false;
      }
    });
  }

  handleCreateDashboard() {
    // Clear any existing draft to ensure a fresh start for new dashboard
    this.userDashboardService.clearDraftFromLocalStorage();
    this.router.navigate(['create-dashboard']);
  }

  handleRefresh() {
    this.loadDashboards();
    this.notificationService.success('Refreshed', 'Dashboard list updated');
  }

  handleCardClick(dashboard: Dashboard) {
    if (dashboard && dashboard.id) {
      this.router.navigate(['create-dashboard'], {
        queryParams: { id: dashboard.id }
      });
    }
  }

  handleCardDoubleClick(dashboard: Dashboard) {
    if (dashboard && dashboard.id) {
      this.router.navigate(['create-dashboard'], {
        queryParams: { id: dashboard.id }
      });
    }
  }

  loadDashboardDetails(dashboard: Dashboard) {
    if (!dashboard || !dashboard.id) {
      return;
    }

    this.isLoadingDashboard = true;
    this.userDashboardService.getDashboardById(dashboard.id).subscribe({
      next: (response: any) => {
        // Handle nested response structure: response.data contains the actual dashboard data
        let dashboardData: any;
        if (response && response.data) {
          dashboardData = response.data;
        } else {
          dashboardData = response;
        }

        // Extract charts from dashboard data
        // API returns PascalCase: Charts (capital C), but also check for lowercase for compatibility
        let charts: ChartData[] = [];
        const chartsArray = dashboardData.Charts || dashboardData.charts;
        if (chartsArray && Array.isArray(chartsArray)) {
          charts = chartsArray.map((chart: any) => {
            // Helper function to clean categories (remove null/undefined values)
            const cleanCategories = (cats: any[]): string[] => {
              if (!Array.isArray(cats)) {
                return [];
              }
              return cats
                .filter((cat: any) => cat !== null && cat !== undefined && cat !== '')
                .map((cat: any) => {
                  if (typeof cat === 'string') {
                    return cat.trim();
                  }
                  return String(cat || 'Unknown').trim();
                })
                .filter((cat: string) => cat.length > 0);
            };

            // API returns PascalCase format: Series, Categories, ChartConfig, ChartId, ChartType, etc.
            // Map them to the expected structure for chart-overview component
            const rawCategories = chart.Categories || [];
            const chartDataObj: any = {
              // Extract series and categories from API (PascalCase)
              series: chart.Series || [],
              categories: cleanCategories(rawCategories), // Clean categories to remove nulls
              table_data: chart.TableData || [],
              // Preserve other fields
              description: chart.Description || {},
              metric_type: chart.MetricType,
              // Extract axis labels from ChartConfig (nested objects use camelCase: xAxis, yAxis)
              xAxisLabel: chart.ChartConfig?.xAxis?.title?.text || '',
              yAxisLabel: chart.ChartConfig?.yAxis?.title?.text || ''
            };

            // Normalize chart data - convert string numeric values to numbers
            const normalizedData = this.normalizeChartData(chartDataObj);

            // Extract and normalize config - API uses PascalCase: ChartConfig
            const rawConfig = chart.ChartConfig || {};
            const normalizedConfig = this.normalizeChartConfig(rawConfig);

            // Preserve table_data
            const tableData = chart.TableData || normalizedData.table_data;

            // Extract title from API (PascalCase: Title)
            const chartTitle = chart.Title
              || chart.ChartConfig?.title?.text
              || 'Untitled Chart';

            const chartData = {
              id: chart.ChartId || chart.id,
              title: chartTitle,
              chart_type: chart.ChartType,
              data: {
                ...normalizedData,
                table_data: tableData // Preserve table_data in the data object
              },
              config: normalizedConfig,
              table_data: tableData, // Also preserve at top level for easy access
              ...extractChartAuditFields(chart),
              ...chartQueryFieldsFromApi(chart),
              DurationBehavior: chart.DurationBehavior,
              no_data_found: chart.NoDataFound ?? false
            };

            return chartData;
          });
        }

        this.selectedDashboard = {
          id: dashboardData.DashboardId || dashboardData.dashboard_id || dashboardData.id || dashboard.id,
          name: dashboardData.DashboardName || dashboardData.dashboard_name || dashboardData.name || dashboard.name,
          charts: charts,
          layout: dashboardData.Layout || dashboardData.layout || dashboard.layout || '2-column',
          created_at: dashboardData.CreatedAt || dashboardData.created_at,
          updated_at: dashboardData.UpdatedAt || dashboardData.updated_at,
          is_shared_with_all: dashboardData.IsSharedWithAll || dashboardData.is_shared_with_all || false
        };

        this.isLoadingDashboard = false;
      },
      error: (error: any) => {
        console.error('Error loading dashboard details:', error);
        this.selectedDashboard = dashboard;
        this.isLoadingDashboard = false;
      }
    });
  }

  handleCardEdit(event: Event, dashboard: Dashboard) {
    event.stopPropagation();
    if (dashboard && dashboard.id) {
      this.router.navigate(['create-dashboard'], {
        queryParams: { id: dashboard.id }
      });
    }
  }

  toggleCardMenu(event: Event, dashboard: Dashboard) {
    event.stopPropagation();
    event.preventDefault();

    if (!dashboard || !dashboard.id) {
      return;
    }

    if (this.selectedMenuDashboard?.id === dashboard.id && this.showCardMenu) {
      // Close menu if clicking the same dashboard's menu button
      this.showCardMenu = false;
      this.selectedMenuDashboard = null;
    } else {
      // Open menu for this dashboard
      this.selectedMenuDashboard = dashboard;
      this.showCardMenu = true;
    }
  }


  handleCardShare(event: Event, dashboard: Dashboard) {
    event.stopPropagation();
    this.showCardMenu = false;

    if (!dashboard || !dashboard.id) {
      this.confirmPopupService.openConfirmDialog({
        title: 'Error',
        message: 'Dashboard ID missing',
        confirmText: 'OK',
        cancelText: '',
        showCancelButton: false
      }).subscribe();
      return;
    }


    const isShared = dashboard.is_shared || dashboard.is_shared_with_all || false;

    if (isShared) {
      this.userDashboardService.unshareDashboard(dashboard.id).subscribe({
        next: () => {
          // this.notificationService.success('Unshared', 'Dashboard unshared');
          this.loadDashboards();
        },
        error: (error: any) => {
          console.error('Error unsharing dashboard:', error);
          const errorMessage = error?.error?.detail || error?.error?.message || 'Unshare failed';
          this.notificationService.error('Error', errorMessage);
        }
      });
    } else {
      this.userDashboardService.shareDashboard(dashboard.id).subscribe({
        next: () => {
          this.notificationService.success('Shared', 'Dashboard shared');
          this.loadDashboards();
        },
        error: (error: any) => {
          console.error('Error sharing dashboard:', error);
          const errorMessage = error?.error?.detail || error?.error?.message || 'Share failed';
          this.notificationService.error('Error', errorMessage);
        }
      });
    }
  }

  handleCardDelete(event: Event, dashboard: Dashboard) {
    event.stopPropagation();
    this.showCardMenu = false;

    if (!dashboard || !dashboard.id) {
      this.notificationService.error('Error', 'Dashboard ID missing');
      return;
    }


    this.confirmPopupService.openConfirmDialog({
      title: 'Delete Dashboard',
      message: `Are you sure you want to delete "${dashboard.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonClass: 'delete-button'
    }).subscribe((result) => {
      if (result === true) {
        this.userDashboardService.deleteDashboard(dashboard.id!).subscribe({
          next: () => {
            this.notificationService.success('Deleted', 'Dashboard deleted');
            this.loadDashboards();
            if (this.selectedDashboard?.id === dashboard.id) {
              this.selectedDashboard = null;
            }
          },
          error: (error: any) => {
            console.error('Error deleting dashboard:', error);
            const errorMessage = error?.error?.detail || error?.error?.message || 'Delete failed';
            this.notificationService.error('Error', errorMessage);
          }
        });
      }
    });
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return formatDateTimeInTimezone(date, 'EST');
    } catch {
      return 'N/A';
    }
  }

  formatUserIdentifier(identifier?: string | null): string {
    if (!identifier) return 'N/A';

    // Handle Azure AD format: AzureAD_username#EXT#@domain.onmicrosoft.com
    // Extract username part (e.g., "samruddhi_rozie.ai" from "AzureAD_samruddhi_rozie.ai#EXT#@AzureADIntegrationRozie.onmicrosoft.com")
    if (identifier.startsWith('AzureAD_')) {
      const extIndex = identifier.indexOf('#EXT#');
      if (extIndex > 0) {
        // Extract the username part between "AzureAD_" and "#EXT#"
        const username = identifier.substring(8, extIndex); // 8 is length of "AzureAD_"
        return username;
      }
    }

    // If it's an email address, extract the name part (before @) and format it nicely
    if (identifier.includes('@')) {
      const emailParts = identifier.split('@');
      const namePart = emailParts[0];
      // Convert email format (e.g., john.doe@example.com) to readable name (John Doe)
      const formattedName = namePart
        .split(/[._-]/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
      return formattedName;
    }

    // If it's not an email, return as is
    return identifier;
  }

  /**
   * Format email address to show a cleaner version
   * For Azure AD format: AzureAD_username#EXT#@domain.onmicrosoft.com -> username
   * For regular emails: user@domain.com -> user
   */
  formatEmail(email?: string | null): string {
    return formatDashboardUserEmail(email) || 'N/A';
  }

  onSearchChange() {
    this.applySearchFilter();
  }

  applySearchFilter() {
    if (!this.searchQuery || this.searchQuery.trim() === '') {
      this.filteredDashboards = [...this.userDashboards];
    } else {
      const query = this.searchQuery.toLowerCase().trim();
      this.filteredDashboards = this.userDashboards.filter(dashboard =>
        dashboard.name.toLowerCase().includes(query)
      );
    }
  }

  shouldShowTooltip(dashboardName: string): boolean {
    // Check if the name is long enough to be truncated
    // This is a simple check - in a real scenario, you might want to check actual rendered width
    return dashboardName.length > 30;
  }

  trackByChartId(index: number, chart: any): any {
    return chart?.id || chart?.chart_id || index;
  }

  normalizeChartData(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const normalized: any = {
      ...data
    };

    // Normalize series data - convert string numeric values to numbers
    if (Array.isArray(data.series)) {
      normalized.series = data.series.map((series: any) => {
        if (!series || !Array.isArray(series.data)) {
          return series;
        }
        const rawData = series.data;
        // Time series: [["2026-01-01T00:00:00+00:00", 24772], ...] - first element can be date string or number
        const isTimeSeries = rawData.length > 0 &&
          Array.isArray(rawData[0]) &&
          rawData[0].length >= 2 &&
          (typeof rawData[0][0] === 'string' && (String(rawData[0][0]).includes('-') || String(rawData[0][0]).includes('T')) || typeof rawData[0][0] === 'number');

        const normalizedData = rawData
          .filter((item: any) => {
            if (Array.isArray(item) && item.length >= 2) {
              return item[1] !== null && item[1] !== undefined;
            }
            if (typeof item === 'object' && item !== null) {
              return (item.y != null) || (item.value != null);
            }
            return item !== null && item !== undefined;
          })
          .map((item: any) => {
            // Time series: convert date string to milliseconds for Highcharts datetime axis
            if (isTimeSeries && Array.isArray(item) && item.length >= 2) {
              const timestamp = item[0];
              const value = typeof item[1] === 'string' ? parseFloat(item[1]) || 0 : (item[1] ?? 0);
              if (typeof timestamp === 'string') {
                const date = new Date(timestamp);
                return isNaN(date.getTime()) ? [timestamp, value] : [date.getTime(), value];
              }
              return [typeof timestamp === 'number' ? timestamp : parseFloat(timestamp) || 0, value];
            }
            // Pie/donut: [["name", value], ...]
            if (Array.isArray(item) && item.length >= 2) {
              const name = (item[0] === null || item[0] === undefined || item[0] === '') ? 'Unknown' : item[0];
              const numValue = typeof item[1] === 'string' ? parseFloat(item[1]) || 0 : (item[1] ?? 0);
              return [name, numValue];
            }
            // Objects with y or value
            if (typeof item === 'object' && item !== null) {
              const name = item.name ?? item.label ?? 'Unknown';
              const value = item.y !== undefined ? item.y : item.value;
              const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (value ?? 0);
              return { name, y: numValue };
            }
            if (typeof item === 'number') return item;
            if (typeof item === 'string' && !isNaN(parseFloat(item)) && isFinite(parseFloat(item))) {
              return parseFloat(item);
            }
            return item;
          });

        return { ...series, data: normalizedData };
        return series;
      });
    }
    return normalized;
  }

  normalizeChartConfig(config: any): any {
    if (!config || typeof config !== 'object') {
      return config;
    }

    const normalized: any = {
      ...config
    };

    // Normalize config.series data - convert string numeric values to numbers
    if (Array.isArray(config.series)) {
      normalized.series = config.series.map((series: any) => {
        if (series && Array.isArray(series.data)) {
          return {
            ...series,
            data: series.data.map((item: any) => {
              // Handle simple numeric values (strings that represent numbers)
              if (typeof item === 'string' && !isNaN(parseFloat(item)) && isFinite(parseFloat(item))) {
                return parseFloat(item);
              }
              // Handle simple numeric values (already numbers)
              if (typeof item === 'number') {
                return item;
              }
              // Handle objects with y or value properties
              if (typeof item === 'object' && item !== null) {
                const normalizedItem: any = { ...item };
                // Convert y value from string to number if it's a string
                if (item.y !== undefined) {
                  normalizedItem.y = typeof item.y === 'string' ? parseFloat(item.y) || 0 : item.y;
                }
                // Convert value from string to number if it's a string
                if (item.value !== undefined) {
                  normalizedItem.value = typeof item.value === 'string' ? parseFloat(item.value) || 0 : item.value;
                }
                // Handle null / empty names (legend, tooltip, axis labels)
                if (
                  normalizedItem.name === null ||
                  normalizedItem.name === undefined ||
                  normalizedItem.name === ''
                ) {
                  normalizedItem.name = 'Unknown';
                }
                return normalizedItem;
              }
              // Handle arrays (for pie charts: [name, value])
              if (Array.isArray(item) && item.length >= 2) {
                const [name, value] = item;
                const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
                const normalizedName = (name === null || name === undefined || name === '') ? 'Unknown' : name;
                return [normalizedName, numValue];
              }
              return item;
            })
          };
        }
        return series;
      });
    }

    return normalizeHighchartsApiConfig(normalized, normalized.chart?.type);
  }
}
